// FILE: assets/bold-price-display.js
if (!customElements.get('bold-price-display')) {
  customElements.define('bold-price-display', class BoldPriceDisplay extends HTMLElement {
    constructor() {
      super();
      this.formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
      this.EVENTS = {
        PRICE_RECALCULATED: 'hub:price:recalculated',
        QTY_TOTAL_UPDATED: 'hub:qty:total_updated'
      };
      // Persists open/closed state per section label across re-renders.
      // Defaults to closed — sections only open when the user explicitly opens them.
      this._detailsOpenState = {};
    }

    connectedCallback() {
      // Set fixed styles immediately (as per your current component structure)
      this.style.display = 'block';
      this.style.marginBottom = '20px';

      const adapter = document.querySelector('bold-sc-adapter');
      this.facade = adapter ? adapter.facade : null;

      // Define the initialization trigger function
      const startInitialization = () => {
        // 1. Render the base HTML structure
        this._renderBaseStructure();

        // 2. Start watching the DOM for button stability before running _moveSubmitButton
        this._setupButtonObserver();

        // 3. Set up the Facade event listeners for continuous updates
        this._initListeners();
      };

      // Check if the Facade is present to start initialization
      if (this.facade && adapter.getAttribute("options") === "ready") {
        startInitialization();
      } else {
        // If not, wait for the hub:options:ready event (Standard BOLD Facade connection)
        document.addEventListener('hub:options:ready', () => {
          const tryInit = (attempt = 0) => {
            const a = document.querySelector('bold-sc-adapter');
            this.facade = a ? a.facade : null;
            if (this.facade || attempt >= 5) {
              startInitialization();
            } else {
              setTimeout(() => tryInit(attempt + 1), 100);
            }
          };
          tryInit();
        }, { once: true });
      }
    }

    _initListeners() {
      // 1. Bind and subscribe to PRICE_RECALCULATED for the main price body
      this._updateDisplay = this._updateDisplay.bind(this);
      document.addEventListener(this.EVENTS.PRICE_RECALCULATED, this._updateDisplay);

      // 2. Bind and subscribe to QTY_TOTAL_UPDATED for only the quantity slot
      this._updateQty = this._updateQty.bind(this);
      document.addEventListener(this.EVENTS.QTY_TOTAL_UPDATED, this._updateQty);
    }

    disconnectedCallback() {
      // 2. CLEANUP: Ensure listener for the main price event is removed
      document.removeEventListener(this.EVENTS.PRICE_RECALCULATED, this._updateDisplay);
      document.removeEventListener(this.EVENTS.QTY_TOTAL_UPDATED, this._updateQty);

      // NEW: Clean up the Mutation Observer
      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
      }
    }

    _init() {
      this._renderBaseStructure();
      this._moveSubmitButton();

      // 3. UPDATED: Subscribe ONLY to PRICE_RECALCULATED for the main price body
      this._updateDisplay = this._updateDisplay.bind(this);
      document.addEventListener(this.EVENTS.PRICE_RECALCULATED, this._updateDisplay);

      // 4. NEW: Subscribe to QTY_TOTAL_UPDATED for only the quantity slot
      this._updateQty = this._updateQty.bind(this);
      document.addEventListener(this.EVENTS.QTY_TOTAL_UPDATED, this._updateQty);

      this._setupButtonObserver();
      // Initial Render — defer until the facade has a real price to show,
      // avoiding a $0.00 flash on slow connections.
      const waitForPrice = (attempt = 0) => {
        const price = this.facade && this.facade.state
          ? (this.facade.state.getSnapshot().originalPrice || 0) : 0;
        if (price > 0 || attempt >= 10) {
          this._updateDisplay();
          this._updateQty();
        } else {
          setTimeout(() => waitForPrice(attempt + 1), 150);
        }
      };
      waitForPrice();
    }

    _executeInitialRenderAndMove() {
      // This is the function that runs only ONCE when the button is found to be stable.

      // 1. Execute DOM Manipulation
      this._moveSubmitButton();

      // 2. Run initial renders
      this._updateDisplay();
      this._updateQty();
    }

    // 5. NEW METHOD: Only updates quantity slot for efficiency
    _updateQty() {
      if (!this.facade) return;
      const qtyEl = this.querySelector('[data-slot="qty"]');

      // Use the new O(1) getter for Quantity
      if (qtyEl) qtyEl.textContent = this.facade.state.getQuantity();
    }

    _setupButtonObserver() {
      // Select the main product form or a stable wrapper node.
      const targetNode = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (!targetNode) {
        console.warn("[Price Display] Could not find the main form to observe.");
        return;
      }

      // Configuration: Watch for any additions/deletions of children and attribute changes.
      const config = { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] };

      // The Callback function runs when a mutation occurs.
      const observerCallback = (mutationsList, observer) => {
        // Iterate through all changes that happened in this observation window.
        for (const mutation of mutationsList) {
          // Check if the change could involve our target button being added, removed, or modified.
          if (mutation.type === 'childList' || mutation.type === 'attributes') {
            const buttonSelector = '.product-form__buttons, [name="add"]';
            const stableButton = targetNode.querySelector(buttonSelector);

            if (stableButton) {
              // Success! The button is stable (found in the DOM).

              // 1. DISCONNECT the observer immediately to prevent memory leaks/redundant checks.
              observer.disconnect();

              // 2. Execute the critical logic.
              this._executeInitialRenderAndMove();

              // Exit the loop after success.
              return;
            }
          }
        }
      };

      // Instantiate and start observing
      this.buttonObserver = new MutationObserver(observerCallback);
      this.buttonObserver.observe(targetNode, config);
    }

    _renderBaseStructure() {
      if (this.querySelector('.bold-price-card')) return;

      this.innerHTML = `
        <div class="bold-price-card">
          <div class="bold-card-header">PRODUCT SUMMARY</div>
          <div class="bold-price-section">
            <div class="bold-row"><span data-label="base" class="bold-label">Base Price</span><span data-slot="base" class="bold-value">-</span></div>
            <div class="bold-row" data-row="options" style="display: none;"></div>
            <div class="bold-row bold-row-unit" data-row="configured" style="display:none;"><span class="bold-label">Item Price w/ Customizations</span><span data-slot="configured-price" class="bold-value">-</span></div>
            <div class="bold-row bold-row-sizes" data-row="sizes" style="display: none; margin-top: 5px;"></div>
          </div>
          <div class="bold-price-footer"><div class="bold-footer-row"><div class="bold-footer-info"><div class="bold-footer-qty">Total Qty: <strong data-slot="qty">1</strong></div><div class="bold-footer-total"><span data-slot="grand-total" class="grand-total-value">-</span></div></div><div class="bold-footer-action" data-slot="button-container"></div></div></div>
        </div>`;
    }

    _moveSubmitButton() {
      const container = this.querySelector('[data-slot="button-container"]');
      const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (!container || !form) return;

      // Identify the stable wrapper that hosts the submit button(s)
      const originalWrapper = form.querySelector('.product-form__buttons');

      // Fallback: If the theme doesn't use the wrapper, find the buttons and clone the first one's parent
      if (!originalWrapper) {
        return;
      }

      // Move the entire wrapper element
      if (!container.contains(originalWrapper)) {
        // Use appendChild to move the node to the new location.
        container.appendChild(originalWrapper);

        // Ensure the wrapper occupies the full width of the display container
        originalWrapper.style.width = '100%';
        originalWrapper.style.margin = '0';
      }
    }

    // FILE: assets/bold-sc-price-display.js (inside _renderDetails)
    _renderDetails(label, total, breakdown) {
      const details = document.createElement('details');
      const summary = document.createElement('summary');
      summary.style.cssText = 'display: flex; justify-content: space-between; cursor: pointer; list-style: none; outline: none; color: #666; width: 100%;';

      let summaryHtmlTotal;

      // Special handling for the Options row: show original vs discounted total.
      if (
        label === 'Options' &&
        this.facade &&
        this.facade.state &&
        typeof this.facade.state.getOptionOriginalExtras === 'function'
      ) {
        const currentExtras = total || 0;
        const originalExtras = this.facade.state.getOptionOriginalExtras() || 0;

        if (originalExtras > currentExtras && currentExtras > 0) {
          const originalStr = this.formatter.format(originalExtras / 100);
          const currentStr = this.formatter.format(currentExtras / 100);

          summaryHtmlTotal =
            `<s style="color:#bbb;font-size:0.9em;margin-right:6px;">${originalStr}</s>` +
            `<span>${currentStr}</span>`;
        } else {
          summaryHtmlTotal = currentExtras > 0
            ? this.formatter.format(currentExtras / 100)
            : '-';
        }
      } else {
        // Default behavior for Sizes and everything else
        summaryHtmlTotal = total > 0
          ? this.formatter.format(total / 100)
          : '-';
      }

      summary.innerHTML = `<span>${label} ▾</span> <span>${summaryHtmlTotal}</span>`;

      const list = document.createElement('div');
      list.style.cssText = 'padding: 10px 0 6px 12px; font-size: 0.9em; color: #444; border-left: 2px solid #eee; margin-top: 4px; display: flex; flex-direction: column; gap: 8px;';

      breakdown.forEach(item => {
        const line = document.createElement('div');
        line.style.cssText = 'display: flex; justify-content: space-between; align-items: flex-start;';

        let labelText = item.label;

        if (label === 'Sizes' && this.facade) {
          labelText = this.facade.helpers.normalizeSizeLabel(item.label);
        }

        const qtyLabel = item.qty > 1 ? `<strong>${item.qty}</strong> x ${labelText}` : labelText;
        const displayPrice = item.linePrice !== undefined ? item.linePrice : item.price;
        const priceStr = displayPrice > 0 ? this.formatter.format(displayPrice / 100) : '-';

        const unitRateStr = item.unitPrice > 0 ? ` + ${this.formatter.format(item.unitPrice / 100)}/ea` : '';
        let unitRateHtml = unitRateStr;

        // Special Pricing (SP) view: when we have a higher previous unit price for options,
        // show it as a strikethrough before the current price.
        if (label === 'Options' && item.spPreviousUnitPrice && item.spPreviousUnitPrice > item.unitPrice) {
          const prev = this.formatter.format(item.spPreviousUnitPrice / 100);
          const curr = this.formatter.format(item.unitPrice / 100);
          unitRateHtml = ` + <s style="color:#bbb;font-size:0.9em;margin-right:6px;">${prev}/ea</s> <span>${curr}/ea</span>`;
        }

        let leftCol;
        let rightCol;

        // 🛠️ CONDITIONAL VIEW LOGIC
        if (label === 'Options') {
          // OPTIONS VIEW: Unit Price is displayed beneath the Line Total.
          leftCol = `<div><span>${qtyLabel}</span></div>`;

          rightCol = `
            <div style="text-align: right; white-space: nowrap;">
              <span>${unitRateHtml || '-'}</span>
            </div>
          `;
        } else {
          // SIZES VIEW: Unit Price is displayed beneath the item label on the left.
          leftCol = `<div style="display: flex; flex-direction: column;">
              <span>${qtyLabel}</span>
              ${unitRateStr ? `<span style="font-size: 0.9em; color: #888; margin-top: 2px;">${unitRateStr}</span>` : ''}
            </div>`;

          rightCol = `<div style="text-align: right;"><span>${priceStr}</span></div>`;
        }

        line.innerHTML = `${leftCol} ${rightCol}`;
        list.appendChild(line);
      });

      // Restore open/closed state — default closed, persist user preference
      if (this._detailsOpenState[label]) {
        details.setAttribute('open', '');
      }
      details.addEventListener('toggle', () => {
        this._detailsOpenState[label] = details.open;
      });
      details.appendChild(summary);
      details.appendChild(list);
      return details;
    }

    // ---------------------------------------------------------------------
    // OPTIONS MERGE IN EXACT VISUAL ORDER
    // ---------------------------------------------------------------------
    // Produces a single Options breakdown list ordered by facade option sets:
    // - If an option has priced breakdown rows, include those.
    // - Else if it has a plain text/textarea selection value, include it as a
    //   display-only row (unitPrice/linePrice 0 -> '-' in UI).
    _getOrderedMergedOptionBreakdown() {
      if (!this.facade || !this.facade.state || !this.facade.options) return [];

      // 1. Get currently calculated priced items and current selections
      const priced = this.facade.state.getOptionBreakdown() || [];
      const selections = this.facade.state.getSelection();
      if (!selections || typeof selections !== 'object') return priced;

      // 2. Get option definitions in stable visual order
      let defs = [];
      try { defs = this.facade.options.getOptionSets() || []; } catch (e) { defs = []; }

      // 3. Determine matrix-controlled keys (Sizes) so we don't duplicate them here
      let matrixKeysLower = [];
      try {
        const matrix = this.facade.state.getMatrix ? this.facade.state.getMatrix() : {};
        matrixKeysLower = matrix && typeof matrix === 'object'
          ? Object.keys(matrix).map(k => String(k).replace(/\*/g, '').trim().toLowerCase())
          : [];
      } catch (e) {
        matrixKeysLower = [];
      }

      // 4. Group priced rows by option key for easy lookup
      const pricedByKey = new Map();
      priced.forEach((it) => {
        const raw = String(it?.label || '');
        const keyPart = raw.split(':')[0].replace(/\*/g, '').trim().toLowerCase();
        if (!keyPart) return;
        const arr = pricedByKey.get(keyPart) || [];
        arr.push(it);
        pricedByKey.set(keyPart, arr);
      });

      const out = [];
      const seen = new Set();

      // 5. Walk through definitions to build the breakdown in the correct order
      defs.forEach((d) => {
        const pretty = String(d?.prettyName || d?.name || '').replace(/\*/g, '').trim();
        if (!pretty) return;

        const keyLower = pretty.toLowerCase();
        if (seen.has(keyLower)) return;
        seen.add(keyLower);

        // Skip keys that belong in the 'Sizes' section
        if (matrixKeysLower.includes(keyLower)) return;

        // A) If the item is already in the 'priced' list (has a cost), use that data
        const pricedRows = pricedByKey.get(keyLower);
        if (pricedRows && pricedRows.length) {
          out.push(...pricedRows);
          pricedByKey.delete(keyLower);
          return;
        }

        // B) NEW SPRINT LOGIC: If item is not priced, check if it's selected
        let rawVal = selections[pretty];
        if (rawVal === undefined) {
          const foundKey = Object.keys(selections).find((k) =>
            String(k).replace(/\*/g, '').trim().toLowerCase() === keyLower
          );
          if (foundKey) rawVal = selections[foundKey];
        }

        // Skip if nothing is selected or if it's a reset state
        const isEmpty =
          rawVal == null ||
          rawVal === '' ||
          rawVal === 'Unchecked' ||
          (Array.isArray(rawVal) && rawVal.length === 0);

        if (isEmpty) return;

        // C) Force add to the list as a $0 / display-only item
        const rawValStr = Array.isArray(rawVal) ? rawVal.join(', ') : String(rawVal);
        // Strip Bold's inline price annotations (e.g. "[+$ 3.95]", "(+$5.00)")
        // from the display label — the price is already shown on the right side.
        const valStr = rawValStr
          .replace(/\s*\[.*?\]/g, '')       // remove [+$ 3.95] or [In Stock] etc.
          .replace(/\s*\(\s*[\+\$][^)]*\)/g, '') // remove (+$5.00) style
          .replace(/\s*\+\s*\$?\d+(\.\d{2})?\s*$/, '') // remove trailing +$5.00
          .trim();

        out.push({
          label: `${pretty}: ${valStr}`,
          qty: 1,
          unitPrice: 0,
          linePrice: 0
        });
      });

      // 6. Safety: append any remaining priced items that didn't match definitions
      for (const arr of pricedByKey.values()) {
        out.push(...arr);
      }

      return out;
    }

    _updateDisplay() {
      if (!this.facade) return;
      // 6. OPTIMIZED: Use O(1) getters for all frequently used values
      const state = this.facade.state.getSnapshot();
      const format = (cents) => this.formatter.format(cents / 100);

      const baseEl = this.querySelector('[data-slot="base"]');
      if (baseEl) {
        const original = state.originalPrice || 0;
        const current = state.currentPrice || 0;
        const label = this.querySelector('[data-label="base"]');
        if (current < original) {
          baseEl.innerHTML = `<s style="color:#bbb;font-size:0.9em;margin-right:6px;">${format(original)}</s> <span style="color:#d32f2f; font-weight:500;">${format(current)}</span>`;
          if (label) label.textContent = "Quantity Discount Price";
        }
        else {
          baseEl.textContent = format(current);
          if (label) label.textContent = "Base Price";
        }

        const qty = this.facade.state.getQuantity();
        /* Removed to always show base price at init
          if (qty <= 0) baseEl.closest('.bold-row').style.display = 'none';
          else baseEl.closest('.bold-row').style.display = 'flex';
        */
       baseEl.closest('.bold-row').style.display = 'flex';
      }

      const sizeRow = this.querySelector('[data-row="sizes"]');
      if (sizeRow) {
        // Use O(1) getter for size extras
        const sizeBreakdown = this.facade.state.getSizeBreakdown();
        const sizeExtras = this.facade.state.getSizeExtras();
        if (sizeBreakdown && sizeBreakdown.length > 0) {
          sizeRow.style.display = 'block';
          sizeRow.innerHTML = '';
          sizeRow.appendChild(this._renderDetails('Sizes', sizeExtras, sizeBreakdown));
        } else {
          sizeRow.style.display = 'none';
        }
      }

      const optRow = this.querySelector('[data-row="options"]');
      if (optRow) {
        // ✅ merged in exact visual order
        const mergedOptionBreakdown = this._getOrderedMergedOptionBreakdown();
        const optionExtras = this.facade.state.getOptionExtras();

        if (mergedOptionBreakdown && mergedOptionBreakdown.length > 0) {
          optRow.style.display = 'block';
          optRow.innerHTML = '';
          // Note: total remains optionExtras (priced options). Free text items
          // display with '-' pricing and do not affect totals.
          optRow.appendChild(this._renderDetails('Options', optionExtras, mergedOptionBreakdown));
        } else {
          optRow.style.display = 'none';
        }
      }

      const configRow = this.querySelector('[data-row="configured"]');
      const configEl = this.querySelector('[data-slot="configured-price"]');
      // Use O(1) getter for configured price
      const configuredPrice = this.facade.state.getConfiguredPrice();

      if (configRow && configEl) {
        if (state.optionExtras > 0) { // Still relying on optionExtras value
          configEl.textContent = format(configuredPrice);
          configRow.style.display = 'flex';
          if (state.sizeBreakdown && state.sizeBreakdown.length > 0) { configRow.style.borderBottom = ''; configRow.style.marginBottom = ''; configRow.style.paddingBottom = ''; }
          else { configRow.style.borderBottom = 'none'; configRow.style.marginBottom = '0'; configRow.style.paddingBottom = '0'; }
        } else { configRow.style.display = 'none'; }
      }

      // 7. OPTIMIZED: Use O(1) getters for Grand Total
      const grandTotalEl = this.querySelector('[data-slot="grand-total"]');
      if (grandTotalEl) grandTotalEl.textContent = this.facade.state.getFormattedGrandTotal();

      const section = this.querySelector('.bold-price-section');
      if (section) {
        const hasVisible = Array.from(section.children).some(child => {
          return window.getComputedStyle(child).display !== 'none';
        });
        section.style.display = hasVisible ? 'block' : 'none';
      }
    }
  });

  if (!document.querySelector('bold-price-display')) {
    document.addEventListener('DOMContentLoaded', () => {
      const buyButtons = document.querySelector('.product-form__buttons, [name="add"]');
      if (buyButtons && buyButtons.parentNode) {
        const display = document.createElement('bold-price-display');
        buyButtons.parentNode.insertBefore(display, buyButtons);
      }
    });
  }
}